import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Home, Hammer, Wrench, Droplets, Wind } from 'lucide-react';

const services = [
  {
    icon: <Home />,
    title: 'Dachneubau',
    description: 'Professionelle Errichtung neuer Dächer mit hochwertigen Materialien',
    details: ['Komplette Planung', 'Hochwertige Materialien', 'Fachgerechte Installation']
  },
  {
    icon: <Hammer />,
    title: 'Dachsanierung',
    description: 'Fachgerechte Sanierung und Modernisierung bestehender Dächer',
    details: ['Dachzustand-Analyse', 'Unterkonstruktion-Sanierung', 'Moderne Dämmung']
  },
  {
    icon: <Wrench />,
    title: 'Dachreparatur',
    description: 'Schnelle und zuverlässige Reparatur von Dachschäden',
    details: ['Notfall-Service', 'Schnelle Behebung', 'Professionelle Ausführung']
  },
  {
    icon: <Droplets />,
    title: 'Abdichtung',
    description: 'Professionelle Abdichtungsarbeiten für langanhaltenden Schutz',
    details: ['Hochwertige Systeme', 'Wasserdicht', 'Langlebig']
  },
  {
    icon: <Wind />,
    title: 'Fassadengestaltung',
    description: 'Moderne Fassadensanierung mit Wärmedämmung',
    details: ['Energieeffizient', 'Stilvolle Verkleidung', 'Professionell']
  }
];

export const ServicesSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="leistungen" ref={ref} className="py-12 sm:py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-red-100 to-transparent opacity-50 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-red-100 to-transparent opacity-50 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-6">
            Unsere
            <span className="block bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Leistungen
            </span>
          </h2>
          <p className="text-base sm:text-xl text-gray-700 max-w-3xl mx-auto px-2">
            Umfassende Dachdeckerleistungen aus einer Hand
          </p>
        </motion.div>

        {/* Services Grid - Responsive */}
        <div className="space-y-4 sm:space-y-8">
          {/* Top Row - 3 Services on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {services.slice(0, 3).map((service, index) => (
              <ServiceCard key={index} service={service} index={index} isInView={isInView} />
            ))}
          </div>
          
          {/* Bottom Row - 2 Services Centered */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto">
            {services.slice(3, 5).map((service, index) => (
              <ServiceCard key={index + 3} service={service} index={index + 3} isInView={isInView} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-8 sm:mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.button
            onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-red-600 to-red-800 text-white text-base sm:text-lg font-bold rounded-lg shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Anfrage stellen
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, isInView }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={isInView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="group relative bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300"
    whileHover={{ y: -5 }}
  >
    {/* Gradient Border Effect - Desktop only */}
    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-red-600 to-red-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden sm:block" style={{ padding: '2px' }}>
      <div className="w-full h-full bg-white rounded-xl sm:rounded-2xl"></div>
    </div>

    <div className="relative z-10">
      {/* Icon */}
      <motion.div
        className="inline-flex p-3 sm:p-4 bg-gradient-to-br from-red-600 to-red-800 rounded-lg sm:rounded-xl mb-4 sm:mb-6 shadow-lg"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.3 }}
      >
        {React.cloneElement(service.icon, { size: 24, className: 'text-white sm:w-8 sm:h-8' })}
      </motion.div>

      {/* Content */}
      <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-gray-900">{service.title}</h3>
      <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">{service.description}</p>

      {/* Bullet Points - Compact on mobile */}
      <ul className="space-y-1 sm:space-y-2">
        {service.details.map((detail, idx) => (
          <li key={idx} className="flex items-center space-x-2 text-gray-700 text-sm sm:text-base">
            <span className="text-red-600">•</span>
            <span>{detail}</span>
          </li>
        ))}
      </ul>

      {/* Hover Arrow - Desktop only */}
      <motion.div
        className="mt-4 text-red-600 font-semibold items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex"
      >
        <span>Mehr erfahren</span>
        <motion.span animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1 }}>→</motion.span>
      </motion.div>
    </div>
  </motion.div>
);
